// Channels and Meta routers, from any page. The channel and router pages also use the hooks at the end
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAssistantModule } from "@/components/assistant/context"
import type { AssistantModule, ModuleCatalogEntry, ToolPolicy } from "@/lib/assistant/types"
import { CHANNELS_CHANGED_EVENT, makeChannelsTools, type ChannelsChange, type ChannelsContext } from "./tools"
import { CHANNELS_INSTRUCTIONS } from "./prompt"

export { CHANNELS_CHANGED_EVENT, channelProblems, type ChannelsChange, type ChannelsContext } from "./tools"

export const channelsCatalog: ModuleCatalogEntry = {
  id: "channels",
  title: "Channels and routers",
  route: "/settings/channels",
  description: "Always available, from any page: create and configure channels (Telerivet, WhatsApp, Messenger, Instagram, Telegram), connect them to agents, and route Meta assets to them.",
  resource: "apikeys",
}

const read: ToolPolicy = { kind: "read", permission: ["apikeys", "read"] }
// Each write asks with its own card, so the review mode doesn't ask again
const write = (action: PermissionAction): ToolPolicy => ({ kind: "write", permission: ["apikeys", action], selfReviewed: true })
const destroy: ToolPolicy = { kind: "destructive", permission: ["apikeys", "delete"], selfReviewed: true }

const policies: Record<string, ToolPolicy> = {
  channel_options: read,
  channels_list: read,
  channel_get: read,
  channel_telegram_status: read,
  routers_list: read,
  channel_create: write("create"),
  channel_update: write("update"),
  channel_connect_telegram: write("update"),
  router_create: write("create"),
  router_update: write("update"),
  channel_delete: destroy,
  router_delete: destroy,
}

const labels = {
  channel_options: "Read channel options",
  channels_list: "List channels",
  channel_get: "Read channel",
  channel_create: "Create channel",
  channel_update: "Update channel",
  channel_delete: "Delete channel",
  channel_connect_telegram: "Connect Telegram webhook",
  channel_telegram_status: "Check Telegram webhook",
  routers_list: "List routers",
  router_create: "Create router",
  router_update: "Update router",
  router_delete: "Delete router",
}

export function makeChannelsModule(ctx: ChannelsContext): AssistantModule {
  return {
    id: "channels", title: "Channels and routers",
    tools: makeChannelsTools(ctx), policies, instructions: CHANNELS_INSTRUCTIONS, labels,
  }
}

// ── On the channel and router pages ─────────────────────────────────────────

// A key that changes when the assistant edits the open record, so the page reloads it.
// A deleted record sends the user back to the list
export function useReloadOnAssistantChange(table: ChannelsChange["table"], id: string | undefined, listRoute: string): number {
  const [version, setVersion] = useState(0)
  const navigate = useNavigate()
  useEffect(() => {
    const onChange = (e: Event) => {
      const change = (e as CustomEvent<ChannelsChange>).detail
      if (!change || change.table !== table || change.id !== id) return
      if (change.deleted) navigate(listRoute)
      else setVersion(v => v + 1)
    }
    window.addEventListener(CHANNELS_CHANGED_EVENT, onChange)
    return () => window.removeEventListener(CHANNELS_CHANGED_EVENT, onChange)
  }, [table, id, listRoute, navigate])
  return version
}

// A key that changes whenever the assistant changes any channel or router, for the list pages
export function useAssistantChangeKey(): number {
  const [key, setKey] = useState(0)
  useEffect(() => {
    const bump = () => setKey(k => k + 1)
    window.addEventListener(CHANNELS_CHANGED_EVENT, bump)
    return () => window.removeEventListener(CHANNELS_CHANGED_EVENT, bump)
  }, [])
  return key
}

// "This channel" for the assistant, and a warning not to overwrite what the user is typing
export function useOpenRecordContext(table: ChannelsChange["table"], id: string | undefined, title: () => string, dirty: () => boolean) {
  const noun = table === "channels" ? "channel" : "router"
  useAssistantModule(`${table}.page`, () => ({
    id: `${table}.page`,
    title: `${noun[0].toUpperCase()}${noun.slice(1)} settings page`,
    tools: {},
    context: id && id !== "new"
      ? `Open ${noun}: ${title() || "Untitled"} (id ${id}).${dirty() ? ` The user has unsaved changes on this page: ask before changing this ${noun}, since reloading it would discard them.` : ""}`
      : `Creating a new ${noun} on this page (not saved yet).`,
  }))
}
